import { useEffect, useState, memo } from 'react';
import { getDownloadUrl } from '@/api/attachments';
import { IS_BACKEND_CONFIGURED } from '@/api/config';

interface Props {
  mapId: string | null;
  attachmentId: string;
  s3Key: string;
  fileName: string;
  x: number;
  y: number;
  width: number;
  height: number;
  borderRadius: number;
  onClickPreview: (src: string, alt: string) => void;
}

export const ImageThumbnail = memo(function ImageThumbnail({
  mapId,
  attachmentId,
  s3Key,
  fileName,
  x,
  y,
  width,
  height,
  borderRadius,
  onClickPreview,
}: Props) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!mapId || !IS_BACKEND_CONFIGURED) return;

    let cancelled = false;
    void getDownloadUrl(mapId, attachmentId, s3Key).then((url) => {
      if (!cancelled) setSrc(url);
    }).catch(() => {
      // Ignore errors -- just don't show the preview
    });

    return () => { cancelled = true; };
  }, [mapId, attachmentId]);

  if (!src) {
    // Placeholder while loading
    return (
      <g>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          rx={borderRadius}
          fill="#1e293b"
          stroke="#334155"
          strokeWidth={0.5}
        />
        <text
          x={x + width / 2}
          y={y + height / 2}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={9}
          fill="#64748b"
          fontFamily="system-ui, sans-serif"
          style={{ pointerEvents: 'none' }}
        >
          Loading...
        </text>
      </g>
    );
  }

  return (
    <g
      onClick={(e) => {
        e.stopPropagation();
        onClickPreview(src, fileName);
      }}
      style={{ cursor: 'zoom-in' }}
    >
      <clipPath id={`clip-${attachmentId}`}>
        <rect x={x} y={y} width={width} height={height} rx={borderRadius} />
      </clipPath>
      <image
        href={src}
        x={x}
        y={y}
        width={width}
        height={height}
        preserveAspectRatio="xMidYMid slice"
        clipPath={`url(#clip-${attachmentId})`}
      />
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={borderRadius}
        fill="none"
        stroke="#475569"
        strokeWidth={0.5}
      />
    </g>
  );
});
